import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightning, PencilLine, Microphone, SignOut } from '@phosphor-icons/react';
import { logout } from '../../../services/loginApi';
import { compareSubcourseLevels, resolveLevelSortFromLangCode } from '../../../utils/subcourseSort';
import ProfileWidget from './ProfileWidget';
import OverallCourseWidget from './OverallCourseWidget';
import './ReportStats.css';

const ReportStats = ({ data, loading, error }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      
      // Redirect to learner login
      console.log('👋 Logging out...');
      navigate('/learner/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect even if API call fails
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      navigate('/learner/login');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const ProgressBar = ({ value, color }) => (
    <div className="progress-bar-wrapper">
      <div className="progress-bar-track">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      <span className="progress-bar-text">{value}%</span>
    </div>
  );

  if (loading) {
    return (
      <div className="report-stats-container">
        <div className="report-loading">
          <div className="loading-spinner"></div>
          <p>Loading your progress report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-stats-container">
        <div className="report-error">
          <svg 
            className="error-icon" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
          <p className="error-message">{error}</p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="report-stats-container">
        <div className="report-empty">
          <svg 
            className="empty-icon" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
            />
          </svg>
          <p>No progress data available yet.</p>
          <p className="empty-subtitle">Start practicing to see your stats!</p>
        </div>
      </div>
    );
  }

  const levelSort = resolveLevelSortFromLangCode(
    data?.[0]?.['lang-code'] ?? data?.[0]?.langCode
  );

  const sortedData = [...data].sort((a, b) => {
    const codeA = a['subcourse-code'] || a.subcourseCode || a.code || a.name || '';
    const codeB = b['subcourse-code'] || b.subcourseCode || b.code || b.name || '';
    return compareSubcourseLevels(
      { code: codeA, order: a.order },
      { code: codeB, order: b.order },
      { levelSort }
    );
  });

  return (
    <div className="report-stats-container">
      <div className="widgets-row">
        <ProfileWidget />
        <OverallCourseWidget data={sortedData} />
      </div>

      <div className="report-table-wrapper">
        <table className="report-table">
          <thead>
            <tr>
              <th>Course Level</th>
              <th>Overall Progress</th>
              <th className="text-center">Level</th>
              <th className="text-center">Exercises</th>
              <th className="text-center">Time Spent</th>
              <th className="text-center">Details</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((subcourse) => {
              const userStats = subcourse['user-stats'] || {};
              const allStats = userStats['all-stats'] || {};
              const quickStats = userStats['quick-stats'] || {};
              const writingStats = userStats['writing-stats'] || {};
              const speakingStats = userStats['speaking-stats'] || {};
              const isExpanded = expandedRow === subcourse._id;

              return (
                <React.Fragment key={subcourse._id}>
                  <tr className={`table-row ${isExpanded ? 'expanded' : ''}`}>
                    <td className="course-cell">
                      <div 
                        className="course-info clickable"
                        onClick={() => setExpandedRow(isExpanded ? null : subcourse._id)}
                      >
                        <div>
                          <div className="course-name">{subcourse.name}</div>
                          <div className="course-description">{subcourse.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="progress-cell">
                      <ProgressBar value={allStats.progress || 0} color="#1d4746" />
                    </td>
                    <td className="text-center">
                      <span className="level-badge">Level {allStats.level || 0}/100</span>
                    </td>
                    <td className="text-center">
                      <span className="stat-value">{allStats.taken || 0}</span>
                    </td>
                    <td className="text-center">
                      <span className="stat-value">{formatDuration(allStats.duration || 0)}</span>
                    </td>
                    <td className="text-center">
                      <button 
                        className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : subcourse._id)}
                      >
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 20 20" 
                          fill="none"
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M19 9l-7 7-7-7" 
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="expanded-row">
                      <td colSpan="6">
                        <div className="expanded-content">
                          <h4 className="expanded-title">Practice Type Breakdown</h4>
                          <table className="detail-table">
                            <thead>
                              <tr>
                                <th>Practice Type</th>
                                <th className="text-center">Level</th>
                                <th>Progress</th>
                                <th className="text-center">Exercises Taken</th>
                                <th className="text-center">Time Spent</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="detail-row quick-practice-row">
                                <td>
                                  <div className="practice-type-cell">
                                    <Lightning size={20} className="practice-icon" />
                                    <span className="practice-name">Quick Practice</span>
                                  </div>
                                </td>
                                <td className="text-center" data-label="Level">
                                  <span className="level-badge small">Level {quickStats.level || 0}/100</span>
                                </td>
                                <td data-label="Progress">
                                  <ProgressBar value={quickStats.progress || 0} color="#3b82f6" />
                                </td>
                                <td className="text-center" data-label="Exercises">
                                  <span className="stat-value">{quickStats.taken || 0}</span>
                                </td>
                                <td className="text-center" data-label="Time Spent">
                                  <span className="stat-value">{formatDuration(quickStats.duration || 0)}</span>
                                </td>
                              </tr>
                              <tr className="detail-row writing-practice-row">
                                <td>
                                  <div className="practice-type-cell">
                                    <PencilLine size={20} className="practice-icon" />
                                    <span className="practice-name">Writing Practice</span>
                                  </div>
                                </td>
                                <td className="text-center" data-label="Level">
                                  <span className="level-badge small">Level {writingStats.level || 0}/100</span>
                                </td>
                                <td data-label="Progress">
                                  <ProgressBar value={writingStats.progress || 0} color="#8b5cf6" />
                                </td>
                                <td className="text-center" data-label="Exercises">
                                  <span className="stat-value">{writingStats.taken || 0}</span>
                                </td>
                                <td className="text-center" data-label="Time Spent">
                                  <span className="stat-value">{formatDuration(writingStats.duration || 0)}</span>
                                </td>
                              </tr>
                              <tr className="detail-row speaking-practice-row">
                                <td>
                                  <div className="practice-type-cell">
                                    <Microphone size={20} className="practice-icon" />
                                    <span className="practice-name">Speaking Practice</span>
                                  </div>
                                </td>
                                <td className="text-center" data-label="Level">
                                  <span className="level-badge small">Level {speakingStats.level || 0}/100</span>
                                </td>
                                <td data-label="Progress">
                                  <ProgressBar value={speakingStats.progress || 0} color="#ec4899" />
                                </td>
                                <td className="text-center" data-label="Exercises">
                                  <span className="stat-value">{speakingStats.taken || 0}</span>
                                </td>
                                <td className="text-center" data-label="Time Spent">
                                  <span className="stat-value">{formatDuration(speakingStats.duration || 0)}</span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="report-logout-container">
        <button onClick={handleLogout} className="report-logout-btn">
          <SignOut size={20} weight="bold" />
          <span>Log Out</span>
        </button>
      </div>
    </div>
  );
};

export default ReportStats;

